import {
  Box,
  Button,
  Flex,
  FormInput,
  FormLabel,
  IconButton,
  Input,
  Select,
  Text,
  Tooltip
} from '@sparrowengg/twigs-react';
import {
  ArrowLeftIcon,
  DeleteIcon,
  GripDotsVerticalIcon,
  PencilIcon,
  PlusIcon
} from '@sparrowengg/twigs-react-icons';
import Fallback from 'commons/components/fall-back';
import TicketIcon from 'commons/icons/ticket';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import UserShield from 'commons/icons/user-shield';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { setIparamApp } from 'redux-store/actions';
import {
  booleanOptions,
  customSelectStyles,
  iparamsDefaultValues,
  iparamsOptions,
  productOptions
} from '../constants';
import { IRootDispatch, IRootState } from 'commons/types';
import { IparamsType } from '../types';
import IparamsModal from './iparams-modal';
import ActionEnergy from 'commons/icons/action-energy';
import { iparamType, productConstants } from 'commons/constants';

const schema = Yup.object().shape({
  displayName: Yup.string().required('Display Name is required'),
  keyName: Yup.string().required('Key Name is required'),
  type: Yup.object().required('Type is required'),
  description: Yup.string().optional(),
  secure: Yup.object().required('Secure is required'),
  required: Yup.object().required('Required is required'),
  product: Yup.object().required('Product is required'),
  skipApi: Yup.object().optional()
});

const IParams = () => {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch<IRootDispatch>();
  const { iparams } = useSelector((state: IRootState) => state.configurations);
  const [showIparamsModal, setShowIparamsModal] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: iparamsDefaultValues
  });
  const appValidation = useSelector((state: IRootState) => state.appValidation);
  const currentDomain =
    window.location.hostname?.split('.')[1] || productConstants.SURVEYSPARROW;

  const onSubmit: SubmitHandler<any> = (data) => {
    const updatedIparams = iparams.some((param) => param.id === data.id)
      ? iparams.map((param) => (param.id === data.id ? { ...data } : param))
      : [...iparams, { ...data, id: iparams.length + 1 }];

    dispatch(setIparamApp(updatedIparams));
    setIsEditing(false);
  };

  const onDragEnd = (event: DragEndEvent) => {
    let newIparams = iparams;
    const activeItemId = event.active?.id;
    const overItemId = event.over?.id;

    if (!activeItemId || !overItemId) {
      return iparams;
    }
    const activeIndex = iparams.findIndex((item) => item.id === activeItemId);
    const overIndex = iparams.findIndex((item) => item.id === overItemId);

    if (activeIndex === -1 || overIndex === -1) {
      return iparams;
    }

    const [removed] = newIparams.splice(activeIndex, 1);
    newIparams.splice(overIndex, 0, removed);

    dispatch(setIparamApp(newIparams));
  };
  const handleEditIparam = (iparam: IparamsType) => {
    setIsEditing(true);
    reset(iparam);
  };

  const handleDeleteIparam = (iparam: IparamsType) => {
    dispatch(setIparamApp(iparams.filter((item) => item.id !== iparam.id)));
  };

  useEffect(() => {
    if (isEditing && currentDomain) {
      setValue(
        'product',
        (productOptions.find(
          (option) => option.domain === currentDomain
        ) as any) ?? productOptions[0]
      );
    }
  }, [isEditing]);

  return (
    <Box
      css={{
        height: 'calc(100vh - 73px)',
        width: '100%',
        background: '#151515'
      }}
    >
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" justifyContent="center">
            <Box css={{ width: 800, marginTop: '$36' }}>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                css={{ position: 'relative' }}
              >
                <Flex flexDirection="column" gap="$2">
                  <Text size="lg" weight="medium" css={{ color: '$white900' }}>
                    New IParams Key
                  </Text>
                  <Text size="sm" css={{ color: '$white600' }}>
                    Fill the below fields to create a new IParams key
                  </Text>
                </Flex>
                <Flex alignItems="center" gap="$4">
                  {!!Object.keys(errors).length && (
                    <Flex gap="$4" alignItems="center">
                      <Box
                        css={{
                          backgroundColor: '$negative600',
                          width: '6px',
                          height: '6px',
                          borderRadius: '$round'
                        }}
                      />
                      <Text css={{ color: '$white600' }} size="xs">
                        Please fill all the fields
                      </Text>
                    </Flex>
                  )}
                  <Button
                    size="lg"
                    type="submit"
                    variant="outline"
                    css={{ background: 'transparent !important' }}
                  >
                    Save IParams Key
                  </Button>
                </Flex>
                <IconButton
                  icon={<ArrowLeftIcon />}
                  size="lg"
                  variant="ghost"
                  onClick={() => setIsEditing(false)}
                  css={{ position: 'absolute', left: '-$29' }}
                />
              </Flex>
              <Flex
                flexDirection="column"
                gap="$6"
                css={{
                  marginTop: '$16',
                  label: {
                    color: '$white800'
                  },
                  'input, textarea': {
                    background: '$white200 !important',
                    color: '$white900',
                    border: '$black300 !important',
                    boxShadow: 'none !important',
                    '&:focus, &:hover, &:active': {
                      background: '$white200'
                    }
                  }
                }}
              >
                <FormInput
                  label="Display Name"
                  size="lg"
                  {...register('displayName')}
                />
                <Flex alignItems="flex-end" gap="$6">
                  <Flex flexDirection="column" gap="$2" css={{ flex: 1 }}>
                    <FormLabel>Key Name</FormLabel>
                    <Input size="lg" {...register('keyName')} />
                  </Flex>
                  <Flex flexDirection="column" gap="$2" css={{ flex: 1 }}>
                    <CustomSelect
                      label="Type"
                      onChange={(value) => {
                        setValue('type', value);
                        if (value?.value === iparamType.API_KEY) {
                          setValue(
                            'skipApi',
                            booleanOptions.find((option) => !option.value)
                          );
                        }
                      }}
                      value={watch('type')}
                      options={iparamsOptions}
                    />
                  </Flex>
                  {(watch('type') as any)?.value === iparamType.API_KEY && (
                    <Flex flexDirection="column" gap="$2" css={{ flex: 1 }}>
                      <CustomSelect
                        label="Skip API"
                        onChange={(value) => {
                          setValue('skipApi', value);
                        }}
                        value={watch('skipApi')}
                        options={booleanOptions}
                      />
                    </Flex>
                  )}
                </Flex>
                <FormInput
                  label="Description"
                  as="textarea"
                  size="lg"
                  css={{ height: 96, resize: 'none' }}
                  {...register('description')}
                />
                <Flex alignItems="center" gap="$6">
                  <CustomSelect
                    label="Secure"
                    onChange={(value) => {
                      setValue('secure', value);
                    }}
                    value={watch('secure')}
                    options={booleanOptions}
                  />
                  <CustomSelect
                    label="Required"
                    onChange={(value) => {
                      setValue('required', value);
                    }}
                    value={watch('required')}
                    options={booleanOptions}
                  />
                  <CustomSelect
                    label="Product"
                    isDisabled
                    onChange={(value) => {
                      setValue('product', value);
                    }}
                    value={watch('product')}
                    options={productOptions}
                  />
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </form>
      ) : (
        <>
          {iparams.length ? (
            <Flex alignItems="center" justifyContent="center">
              <Box css={{ width: 800, marginTop: '$36' }}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Flex flexDirection="column" gap="$2">
                    <Text
                      size="lg"
                      weight="medium"
                      css={{ color: '$white900' }}
                    >
                      Installation Paramteters
                    </Text>
                    <Text size="sm" css={{ color: '$white600' }}>
                      Configure the installation parameters within your
                      applications
                    </Text>
                  </Flex>
                  <Flex alignItems="center" gap="$4">
                    <Tooltip
                      content={
                        !appValidation.isValidated
                          ? 'Save changes to enable simulate.'
                          : ''
                      }
                      size="md"
                    >
                      <Button
                        onClick={() => setShowIparamsModal(true)}
                        leftIcon={<ActionEnergy color="#4A9CA6" />}
                        size="lg"
                        variant="ghost"
                        disabled={!appValidation.isValidated}
                      >
                        Simulate
                      </Button>
                    </Tooltip>
                    <Button
                      size="lg"
                      leftIcon={<PlusIcon />}
                      onClick={() => {
                        setIsEditing(true);
                        reset(iparamsDefaultValues);
                      }}
                    >
                      Add New
                    </Button>
                  </Flex>
                </Flex>
                <Flex
                  flexDirection="column"
                  gap="$6"
                  css={{ marginTop: '$20' }}
                >
                  <DndContext
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext items={iparams}>
                      {iparams.map((iparam) => (
                        <IparamsList
                          iparam={iparam}
                          isFirstElement={iparams.length === 1}
                          handleEditIparam={() => handleEditIparam(iparam)}
                          handleDeleteIparam={() => handleDeleteIparam(iparam)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Flex>
              </Box>
            </Flex>
          ) : (
            <Flex
              alignItems="center"
              flexDirection="column"
              gap="$12"
              justifyContent="center"
              css={{ height: '100%' }}
            >
              <Fallback
                icon={<TicketIcon />}
                isBlackTheme
                title="Create your IParams Key"
                description="IParams(Installation Paraters) is used to fetch required user information upon installation."
              />
              <Button
                size="lg"
                leftIcon={<PlusIcon />}
                onClick={() => setIsEditing(true)}
              >
                New Key
              </Button>
            </Flex>
          )}
        </>
      )}
      {showIparamsModal && (
        <IparamsModal
          currentDomain={currentDomain}
          resetModal={() => setShowIparamsModal(false)}
          iparams={iparams}
        />
      )}
    </Box>
  );
};

export default IParams;

const CustomSelect = ({
  options,
  label,
  onChange,
  isDisabled,
  value
}: {
  label: string;
  options: { label: string; value: string | boolean }[];
  isDisabled?: boolean;
  onChange?: (value: any) => void;
  value: unknown;
}) => {
  return (
    <Flex
      flexDirection="column"
      gap="$2"
      css={{ flex: 1, ...(isDisabled && { cursor: 'not-allowed' }) }}
    >
      <FormLabel>{label}</FormLabel>
      <Select
        size="lg"
        placeholder=""
        isSearchable={false}
        options={options}
        isDisabled={isDisabled}
        css={customSelectStyles}
        onChange={onChange}
        value={value}
      />
    </Flex>
  );
};

const IparamsList = ({
  iparam,
  isFirstElement,
  handleEditIparam,
  handleDeleteIparam
}: {
  iparam: IparamsType;
  isFirstElement: boolean;
  handleEditIparam: () => void;
  handleDeleteIparam: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: iparam.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return (
    <Flex
      key={iparam.keyName}
      alignItems="center"
      ref={setNodeRef}
      style={style}
      justifyContent="space-between"
      css={{
        border: '$borderWidths$xs solid $white300',
        padding: '$6 $8',
        width: 768,
        borderRadius: '$xl',
        position: 'relative',
        '&:hover': {
          background: '$white50'
        }
      }}
    >
      <Flex alignItems="center" gap="$4">
        <UserShield size={24} color="rgba(255, 255, 255, 0.8)" />
        <Text size="md">{iparam.displayName}</Text>
      </Flex>
      <Flex alignItems="center" gap="$4">
        <IconButton
          icon={<PencilIcon />}
          size="sm"
          variant="ghost"
          color="default"
          onClick={handleEditIparam}
        />
        <IconButton
          icon={<DeleteIcon />}
          size="sm"
          variant="ghost"
          color="default"
          onClick={handleDeleteIparam}
        />
      </Flex>
      {!isFirstElement && (
        <Box
          as="i"
          css={{ position: 'absolute', right: '-$16', cursor: 'grab' }}
          {...attributes}
          {...listeners}
        >
          <GripDotsVerticalIcon color="#6A6A6A" size={24} />
        </Box>
      )}
    </Flex>
  );
};
